import { Injectable } from '@angular/core';
import { ApiProvider } from '../providers/api.provider';
import { Observable, throwError } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class TokenJwtService {

  private tokenCheckSubscription: any;
  private refreshInProgress: Observable<string>;
  private invalidTokenHandler: () => void;

  constructor(
    private apiProvider: ApiProvider,
    private router: Router
  ) { }

  startTokenCheck() {
    this.stopTokenCheck();
    this.checkTokenValidity();
    this.startIntervalTokenCheck();
  }

  startIntervalTokenCheck() {
    this.tokenCheckSubscription = setInterval(() => {
      this.performTokenCheck();
    }, 60 * 60 * 1000); // 60 minutes en millisecondes
  }

  stopTokenCheck() {
    if (this.tokenCheckSubscription) {
      clearInterval(this.tokenCheckSubscription);
      this.tokenCheckSubscription = undefined;
    }
  }

  performTokenCheck() {
    this.checkTokenValidity();
  }

  getToken(): string | null {
    return localStorage.getItem('jwtToken');
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) { return true; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof payload.exp !== 'number' || Date.now() >= payload.exp * 1000;
    } catch (_) {
      return true;
    }
  }

  checkTokenValidity() {
    this.renewToken().subscribe(
      () => {},
      error => {
        if (error && error.status === 401) { this.handleInvalidToken(); }
      }
    );
  }

  renewToken(): Observable<string> {
    if (this.refreshInProgress) { return this.refreshInProgress; }
    if (!this.getToken()) { return throwError({ status: 401 }); }
    this.refreshInProgress = this.apiProvider.refreshTpslRouteToken().pipe(
      map(response => {
        if (!response || !response.token) { throw new Error('Token refresh returned no token.'); }
        localStorage.setItem('jwtToken', response.token);
        return response.token;
      }),
      finalize(() => { this.refreshInProgress = undefined; }),
      shareReplay(1)
    );
    return this.refreshInProgress;
  }

  onInvalidToken(handler: () => void): () => void {
    this.invalidTokenHandler = handler;
    return () => {
      if (this.invalidTokenHandler === handler) { this.invalidTokenHandler = undefined; }
    };
  }

  handleInvalidToken(sessionExpired: boolean = true, returnUrl?: string) {
    this.stopTokenCheck();
    if (this.invalidTokenHandler) {
      this.invalidTokenHandler();
      return;
    }
    const user = localStorage.getItem('currentUser');
    let username: string;
    try { username = user && JSON.parse(user).UserName; } catch (_) { /* Invalid stored user. */ }
    localStorage.clear();
    if (username) { localStorage.setItem('lastUserName', username); }
    this.router.navigate(['/login'], { queryParams: { sessionExpired, returnUrl: returnUrl || this.router.url } });
  }
}
